import type { RouteLocationNormalized } from "vue-router";

/*
 * This module allows components throughout the app to register containers
 * as scrollable and associate them to a route.
 *
 * Once registered, the navigation system will use the registered route to
 * retrieve the scrollable containers and save the scrolling positions.
 *
 * When remounting a component, the useScrollable composable will fetch the positions of
 * the scrollable containers and restore them.
 */

/*
 * Types
 */

export namespace Scrolling {
  export type Position = {
    // The id of the scrollable container
    containerId: string;
    // The positions within that container
    x: number;
    y: number;
  };
}

/*
 * Constants
 */

// Registry of mappings between a route and its scrollable containers
//
// Note that the scrollable containers can come from multiple components.
const Registry_ = new Map<string, Set<string>>();

/*
 * Public functions
 */

export const scrolling = {
  // Register container IDs (for a given route) whose scrolling positions
  // should be captured/restored when navigating away from/to this route.
  register: (route: RouteLocationNormalized, containerIds: string[]) => {
    const path = route.path;
    if (Registry_.has(path)) {
      // Add the containerIDs to the existing set
      containerIds.forEach((containerId) =>
        Registry_.get(path)!.add(containerId)
      );
    } else {
      // Create a new set for this route.
      Registry_.set(path, new Set(containerIds));
    }
  },

  // Unregister this route to clear the registry.
  unregister: (route: RouteLocationNormalized) => {
    const path = route.path;
    if (Registry_.has(path)) {
      Registry_.delete(path);
    }
  },

  // Retrieve the list of container IDs for a route
  lookup: (route: RouteLocationNormalized): Set<string> | null => {
    const path = route.path;
    return Registry_.get(path) || null;
  },

  // Capture the scrolling positions for all the scrollable containers associated
  // to the given route.
  capture: (route: RouteLocationNormalized): Scrolling.Position[] => {
    // Retrieve the set of container IDs for the route
    const containerIds = scrolling.lookup(route);
    if (!containerIds) {
      return [];
    }
    // Capture the scrolling positions for the containers
    const positions: Scrolling.Position[] = [];
    containerIds.forEach((containerId) => {
      if (containerId === "window") {
        // Capture the scrolling position for the whole window
        positions.push({
          containerId: "window",
          x: window.scrollX,
          y: window.scrollY,
        });
      } else {
        // Retrieve the container element in the DOM and capture the scrolling
        // position for that container.
        const container = document.getElementById(containerId);
        if (container) {
          positions.push({
            containerId: containerId,
            x: container.scrollLeft,
            y: container.scrollTop,
          });
        }
      }
    });
    return positions;
  },

  // Restore the scrolling position of a given set of containers.
  restore: (positions: Scrolling.Position[]) => {
    positions.forEach((position) => {
      if (position.containerId === "window") {
        window.scrollTo(position.x, position.y);
      } else {
        const container = document.getElementById(position.containerId);
        if (container) {
          // Try using scrollTo first (more reliable in some browsers)
          if (container.scrollTo) {
            try {
              container.scrollTo({
                left: position.x,
                top: position.y,
                behavior: "instant",
              });
            } catch {
              // Fallback to direct property assignment
              container.scrollLeft = position.x;
              container.scrollTop = position.y;
            }
          } else {
            // Fallback to direct property assignment
            container.scrollLeft = position.x;
            container.scrollTop = position.y;
          }
        }
      }
    });
  },

  dump: () => {
    window.console.group("[scrolling.dump] Registry");
    window.console.table(
      Array.from(Registry_.entries()).map(([path, containerIds]) => {
        return {
          path,
          containerIds,
        };
      })
    );
    window.console.groupEnd();
  },
};
